import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Check from '@mui/icons-material/Check';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Refresh from '@mui/icons-material/Refresh';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTheme } from '@mui/material/styles';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import CardHeader from '@mui/material/CardHeader';
import { useAuth0 } from '@auth0/auth0-react';
import { useApolloClient } from '@apollo/client';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { LAST_ORG_USED_KEY } from '../../../utils/ApiConstants';
import { UserContext } from '../../../App';
import { USER_ORGS } from '../../../graphql/queries/user';
import useNRLazyQuery from '../../../graphql/useNRLazyQuery';
import NRTagList from '../../../components/NRTagList/NRTagList';
import useCurrentUserAccess from '../../../hooks/useCurrentUserAccess';
import { mapAccessTagsToNrTags } from '../../../hooks/accessTagIcons';
import { drawerWidth } from '../NRMenu';

/** Wait for React to commit unmounts (e.g. stopPolling cleanups) after isChangingOrg. */
function waitForPaint() {
  return new Promise(resolve => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });
}

const MAX_ORG_FETCH_RETRIES = 3;
const ORG_FETCH_RETRY_DELAY_MS = 1500;

function sortUserOrgs(userOrgs) {
  return [...userOrgs].sort((a, b) => (a?.DisplayName || '').localeCompare(b?.DisplayName || '', undefined, { sensitivity: 'base' }));
}

function NROrgSelector() {
  const theme = useTheme();
  const { currentOrg, setIdToken, setCurrentOrg, currentOrgId, setCurrentOrgId, email, setEmail, setIsChangingOrg, idToken } =
    useContext(UserContext);
  const { displayTags } = useCurrentUserAccess();
  const nrDisplayTags = useMemo(
    () => mapAccessTagsToNrTags(displayTags, theme.palette.text.secondary, 14),
    [displayTags, theme.palette.text.secondary]
  );
  const auth0Namespace = process.env.REACT_APP_AUTH0_NAMESPACE;
  const currentOrgNamespace = auth0Namespace + 'org_name';
  const { getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const { enqueueSnackbar } = useSnackbar();
  const [disableDropdown, setDisableDropdown] = useState(true);
  const [orgs, setOrgs] = useState(null);
  const [retryExhausted, setRetryExhausted] = useState(false);
  const retryCountRef = useRef(0);
  const retryExhaustedRef = useRef(false);
  const client = useApolloClient();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  const [, orgsLoading, orgsCalled, getUserOrgs, , orgsError] = useNRLazyQuery({
    query: USER_ORGS,
    qlObjKey: 'userOrgs',
    hideError: true,
    transformData: userOrgs => {
      const sortedOrgs = sortUserOrgs(userOrgs);
      setOrgs(sortedOrgs);
      setDisableDropdown(sortedOrgs.length < 2);
      retryCountRef.current = 0;
      retryExhaustedRef.current = false;
      setRetryExhausted(false);
      return sortedOrgs;
    }
  });

  function handleManualRetry() {
    retryCountRef.current = 0;
    retryExhaustedRef.current = false;
    setRetryExhausted(false);
    getUserOrgs();
  }

  useEffect(() => {
    if (!idToken?.sub) {
      return;
    }

    retryCountRef.current = 0;
    retryExhaustedRef.current = false;
    setRetryExhausted(false);
    getUserOrgs();
  }, [idToken?.sub, getUserOrgs]);

  useEffect(() => {
    if (!orgsError || orgs?.length || retryExhaustedRef.current) {
      return undefined;
    }

    if (retryCountRef.current >= MAX_ORG_FETCH_RETRIES) {
      retryExhaustedRef.current = true;
      setRetryExhausted(true);
      enqueueSnackbar('Unable to load organizations. Please try again.', { variant: 'error' });
      return undefined;
    }

    const attempt = retryCountRef.current + 1;
    const timer = setTimeout(() => {
      retryCountRef.current = attempt;
      getUserOrgs();
    }, ORG_FETCH_RETRY_DELAY_MS * attempt);

    return () => clearTimeout(timer);
  }, [orgsError, orgs?.length, getUserOrgs, enqueueSnackbar]);

  const hasOrgs = !!orgs?.length;
  const awaitingOrgList = Boolean(idToken?.sub) && !hasOrgs;
  const showLoadingPlaceholder = awaitingOrgList && !retryExhausted && (orgsLoading || !orgsCalled || Boolean(orgsError));
  const showErrorState = awaitingOrgList && retryExhausted && Boolean(orgsError);

  async function handleOnChange(value) {
    if (!value) return;
    try {
      // Unmount route trees (and stop pollers) before swapping the org token so in-flight
      // asset requests cannot run with the new session against the previous org's resources.
      flushSync(() => {
        setIsChangingOrg(true);
      });
      await waitForPaint();

      let idToken = await getAccessTokenSilently({
        ignoreCache: true,
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        organization: value
      });

      if (!!idToken) {
        idToken = await getIdTokenClaims({
          audience: process.env.REACT_APP_AUTH0_AUDIENCE
        });
        await client.clearStore();
        setIdToken(idToken);

        const lastOrgUsedId = localStorage.getItem(idToken.email + LAST_ORG_USED_KEY);

        if (!!idToken.org_id && idToken.org_id !== lastOrgUsedId) {
          localStorage.setItem(idToken.email + LAST_ORG_USED_KEY, idToken.org_id);
          setCurrentOrg(idToken[currentOrgNamespace]);
          setCurrentOrgId(idToken.org_id);
        }

        setEmail(idToken?.email);

        // Leave asset-scoped URLs while still showing the org-change loading gate.
        if (pathname === '/') {
          navigate(0);
        } else {
          navigate('/', { replace: true });
          await waitForPaint();
        }
      }
      handleClose();
    } finally {
      setIsChangingOrg(false);
    }
  }

  /**
   * Menu handling
   */
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const orgButtonSx = {
    textTransform: 'unset',
    ...theme.typography.subtitle2,
    padding: theme.spacing(1),
    maxHeight: theme.spacing(4.5),
    '&.Mui-disabled': {
      color: theme.palette.text.primary,
      backgroundColor: 'unset'
    },
    '& .MuiButton-endIcon': {
      color: theme.palette.text.icon,
      '&>*:nth-of-type(1)': {
        fontSize: theme.spacing(3)
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: `calc(${drawerWidth}px + ${theme.spacing(1)})` }}>
      {showLoadingPlaceholder && (
        <Box
          aria-live="polite"
          aria-busy="true"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            padding: theme.spacing(1),
            maxHeight: theme.spacing(4.5),
            minWidth: theme.spacing(12)
          }}
        >
          <CircularProgress size={16} aria-label="Loading organizations" />
          <Box component="span" sx={{ ...theme.typography.subtitle2, color: theme.palette.text.secondary }}>
            {currentOrg || 'Loading...'}
          </Box>
        </Box>
      )}
      {showErrorState && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
          <Button variant="text" disableElevation color="inherit" disabled sx={orgButtonSx}>
            {currentOrg || 'Organization'}
          </Button>
          <IconButton size="small" color="inherit" onClick={handleManualRetry} aria-label="Retry loading organizations">
            <Refresh fontSize="small" />
          </IconButton>
        </Box>
      )}
      {hasOrgs && (
        <>
          <Button
            id="org-selector-button"
            aria-controls={open ? 'org-selector-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            variant="text"
            disableElevation
            color="inherit"
            onClick={!disableDropdown ? handleClick : undefined}
            endIcon={!disableDropdown && <KeyboardArrowDown />}
            sx={{
              ...orgButtonSx,
              ...(!disableDropdown && { paddingRight: theme.spacing(1.5) })
            }}
            disabled={disableDropdown}
          >
            {currentOrg}
          </Button>
          <Menu
            id="org-selector-menu"
            aria-labelledby="org-selector-button"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left'
            }}
            slotProps={{ paper: { sx: { minWidth: theme.spacing(36.25) } } }}
          >
            <CardHeader
              titleTypographyProps={{ sx: { ...theme.typography.body1 } }}
              subheaderTypographyProps={{ component: 'div' }}
              title={email}
              subheader={nrDisplayTags.length ? <NRTagList tags={nrDisplayTags} maxWidth={theme.spacing(36.25)} /> : null}
              sx={{
                position: 'sticky',
                marginTop: theme.spacing(-1),
                top: 0,
                zIndex: 1,
                backgroundColor: theme.palette.background.paper,
                ...theme.palette.elevation
              }}
            />
            <Divider sx={{ position: 'sticky', top: theme.spacing(nrDisplayTags.length ? 9.485 : 7.235), marginBottom: theme.spacing(0.5) }} />
            {!!orgs &&
              orgs.map(({ Id, DisplayName }) => (
                <MenuItem
                  selected={currentOrgId === Id}
                  sx={{ justifyContent: 'space-between', padding: theme.spacing(1.5, 2) }}
                  onClick={() => currentOrgId !== Id && handleOnChange(Id)}
                  key={Id}
                >
                  {DisplayName}
                  {currentOrgId === Id && <Check sx={{ marginLeft: theme.spacing(1) }} />}
                </MenuItem>
              ))}
          </Menu>
        </>
      )}
    </Box>
  );
}
export default NROrgSelector;
